import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, useSidebar } from '@/components/ui/sidebar'
import { OdinIcon } from '@/components/ui/odin/odin-icon'
import { OdinButton } from '@/components/ui/odin/odin-button'
import { Agent } from 'types/agent'
import { ChatRow } from 'components/chats/ChatRow'
import { useGetNotebookHistory } from 'api/chats'
import { messagesSelectors } from 'store/messages'

type NotebookSidebarProps = {
  agent: Agent
  contextId?: string
  onNewAnalysis: () => void
}

/** Left column of the notebook: agent header, past analyses (history), "new analysis" pinned at the bottom. */
export const NotebookSidebar = ({ agent, contextId, onNewAnalysis }: NotebookSidebarProps) => {
  const { t } = useTranslation()
  const { setOpenMobile } = useSidebar()
  const chatId = useSelector(messagesSelectors.chatId)
  const { data: history = [], isSuccess } = useGetNotebookHistory(agent._id, contextId)

  return (
    <Sidebar collapsible="offcanvas" className="border-sidebar-border">
      <SidebarHeader className="gap-1 px-4 pt-4 pb-3">
        <div className="flex items-center gap-2 text-base font-bold text-foreground">
          <OdinIcon icon={['far', 'sparkles']} className="h-4 w-4 shrink-0 text-primary" />
          <span className="truncate">{agent.name}</span>
        </div>
        {agent.description && <p className="m-0 text-sm text-muted-foreground">{agent.description}</p>}
      </SidebarHeader>

      <SidebarContent className="scrollbar-hidden gap-2 px-3 pb-3">
        <span className="px-1 text-xs tracking-wide text-muted-foreground uppercase">{t('notebookHistoryTitle')}</span>
        {isSuccess && history.length === 0 && (
          <span className="px-1 text-xs text-muted-foreground">{t('notebookHistoryEmpty')}</span>
        )}
        {/* ChatRow's click sets the chat id in the store, exactly like /chat; the wrapper only closes the offcanvas. */}
        <div className="flex flex-col gap-0.5" onClick={() => setOpenMobile(false)}>
          {history.map((chat) => (
            <ChatRow key={chat._id} chat={chat} selected={chat._id === chatId} />
          ))}
        </div>
      </SidebarContent>

      <SidebarFooter className="border-t border-border p-3">
        <OdinButton
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => {
            onNewAnalysis()
            setOpenMobile(false)
          }}
        >
          <OdinIcon icon={['far', 'plus']} className="size-3.5" />
          <span>{t('notebookNewAnalysis')}</span>
        </OdinButton>
      </SidebarFooter>
    </Sidebar>
  )
}
